import React, { useEffect, useRef, useState } from 'react';
import LightBulb from '@gumtree/ui-library/src/assets/light-bulb.svg';
import debounce from '@gumtree/ui-library/src/utils/debounce';
import SwitchWithInfo from '@gumtree/ui-library/src/switch-with-info/switch-with-info';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { BackButton, ContactFormButtons, ContactFormButtonsContainer, ContactFormContainer, ContactFormContent, NextButton, PrivacyNotice, SwitchWithInfoContainer, CheckboxContainer, CheckboxLabel, CheckboxInput, CheckboxText } from './index.style';
import LeadForm from '../../lead-form/LeadForm';
import LeadFormItem from '../../lead-form/LeadFormItem';
import { InfoBox, InfoText, InfoIcon } from '../service-form/index.style';

interface ContactFormData {
    buyerName?: string;
    buyerEmail?: string;
    phoneNumber?: string;
    isMoreProviders?: boolean;
}

interface Props {
    onBack: () => void;
    formData: ContactFormData;
    onFormDataChange: (name: string, value: string | boolean) => void;
    onSubmit: () => void;
    isRequestAQuote?: boolean;
    loading: boolean;
    app?: boolean;
}

const ContactForm: React.FC<Props> = ({
    onBack,
    formData,
    onFormDataChange,
    onSubmit,
    isRequestAQuote = false,
    loading,
    app = false
}) => {
    const formRef = useRef<any>();
    const [showButtons, setShowButtons] = useState(true);

    useEffect(() => {
        if (!app) return;
        const handleResize = () => {
            const isKeyboardOpen = window.visualViewport && window.visualViewport.height < 500;
            setShowButtons(!isKeyboardOpen);
        };
        window.visualViewport?.addEventListener('resize', handleResize);
        return () => window.visualViewport?.removeEventListener('resize', handleResize);
    }, []);

    const isNewUiExperiment = useFeatureValue(GrowthBookFeature.BARK_LAYOUT_OPTIMIZATION, 'A') === 'B';
    
    const handleSendRequestClick = debounce(() => {
        if (!loading && formRef.current && formRef.current.validate()) {
            onSubmit();
        }
    }, 300);

    const handleCheckboxChange = (checked: boolean) => {
        onFormDataChange('isMoreProviders', checked);
    };

    return (
        <ContactFormContainer>
            <ContactFormContent>
                <h2>Contact information</h2>
                <LeadForm ref={formRef}>
                    <LeadFormItem
                        type="input"
                        label="Name"
                        name="buyerName"
                        required
                        minLength={2}
                        maxLength={50}
                        pattern={/^(?!.*\s{2,})(?!.*(.)\1{2,})(?!.*(test|aaa|111|abc|unknown|123|abb))^[a-zA-Z\s'-]+$/i}
                        errorMessage={{
                            required: "Please enter your name",
                            minLength: "You must write at least 2 words",
                            pattern: "Please enter the valid name",
                        }}
                        value={formData.buyerName || ''}
                        onChange={(value) => onFormDataChange('buyerName', value as string)}
                    />
                    <LeadFormItem
                        type="input"
                        label="Email"
                        name="buyerEmail"
                        required
                        pattern={/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/}
                        errorMessage={{
                            required: "Please enter your email",
                            pattern: "Please enter a valid email address",
                        }}
                        value={formData.buyerEmail || ''}
                        onChange={(value) => onFormDataChange('buyerEmail', value as string)}
                    />
                    <LeadFormItem
                        type="input"
                        label="Phone"
                        name="phoneNumber"
                        required
                        pattern={/^(?:\+?44|\(44\)|44|0)?[\s\-()]*([0-9][\s\-()]*){10}$/}
                        errorMessage={{
                            required: "Please enter your phone number",
                            pattern: "Please enter a valid UK mobile number",
                        }}
                        value={formData.phoneNumber || ''}
                        onChange={(value) => onFormDataChange('phoneNumber', value as string)}
                    />
                </LeadForm>
                {isRequestAQuote && (
                    <>
                        {isNewUiExperiment ? (
                            <SwitchWithInfoContainer>
                                <SwitchWithInfo
                                    label="Get more relevant quotes"
                                    info="Get quotes from up to 4 more relevant providers that match your schedule and budget"
                                    value={formData.isMoreProviders ?? false}
                                    onChange={(value) => handleCheckboxChange(value)}
                                />
                            </SwitchWithInfoContainer>
                        ) : (
                            <>
                                <CheckboxContainer>
                                    <CheckboxLabel>
                                        <CheckboxInput
                                            type="checkbox"
                                            checked={formData.isMoreProviders}
                                            onChange={(e) => handleCheckboxChange(e.target.checked)}
                                        />
                                        <CheckboxText>
                                            Yes, I'd like to get quotes from up to 4 more relevant providers.
                                        </CheckboxText>
                                    </CheckboxLabel>
                                </CheckboxContainer>
                                <InfoBox>
                                    <InfoIcon><LightBulb /></InfoIcon>
                                    <InfoText>Make one request and get quotes from up to 5 providers that match your schedule and budget.</InfoText>
                                </InfoBox>
                            </>
                        )}
                    </>
                )}
            </ContactFormContent>
            {showButtons && (
                <ContactFormButtonsContainer>
                    <PrivacyNotice>
                        By clicking "Send request", you consent to sharing your details with Bark to match you with providers. For more details, see{' '}
                        <a href="https://www.bark.com/en/gb/privacy/" target="_blank" rel="noopener noreferrer">
                            Bark's
                        </a>
                        {' '}and{' '}
                        <a href="https://www.gumtree.com/privacy_policy" target="_blank" rel="noopener noreferrer">
                            Gumtree's
                        </a>
                        {' '}privacy policy.
                    </PrivacyNotice>
                    <ContactFormButtons>
                        <BackButton type="button" onClick={onBack}>
                            Back
                        </BackButton>
                        <NextButton type="button" onClick={handleSendRequestClick} disabled={loading}>
                            {loading ? 'Sending...' : 'Send request'}
                        </NextButton>
                    </ContactFormButtons>
                </ContactFormButtonsContainer>
            )}
        </ContactFormContainer>
    )
}

export default ContactForm;
